import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAppVersion } from '@/shared/utils/version';
import { UpdateAvailableToast } from '@/shared/components/notifications/UpdateAvailableToast';

const CHECK_INTERVAL_MS = 60 * 1000;
// How long "Later" silences the prompt before it's shown again.
const SNOOZE_MS = 30 * 60 * 1000;
const TOAST_ID = 'app-update-available';

interface VersionInfo {
  version: string;
  buildId: string;
}

async function fetchDeployedVersion(): Promise<VersionInfo | null> {
  try {
    // Query param + no-store so neither CloudFront nor the browser answers from cache.
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as VersionInfo;
  } catch {
    return null;
  }
}

/**
 * Polls /version.json (emitted by vite.config.ts) and shows a persistent toast once a newer
 * build has been deployed. It never reloads on its own — an automatic reload would drop the
 * user out of a live meeting — the user decides when via "Refresh now".
 */
export function useVersionCheck() {
  useEffect(() => {
    if (import.meta.env.DEV) return;

    let snoozedUntil = 0;
    let inFlight = false;

    const check = async () => {
      if (inFlight || document.hidden || Date.now() < snoozedUntil) return;
      if (toast.isActive(TOAST_ID)) return;

      inFlight = true;
      const deployed = await fetchDeployedVersion();
      inFlight = false;

      if (!deployed?.buildId || deployed.buildId === __APP_BUILD_ID__) return;

      toast(
        ({ closeToast }) => (
          <UpdateAvailableToast
            // Only name the version when this deploy actually bumped it.
            version={deployed.version !== getAppVersion() ? deployed.version : undefined}
            onRefresh={() => window.location.reload()}
            onLater={() => {
              snoozedUntil = Date.now() + SNOOZE_MS;
              closeToast();
            }}
          />
        ),
        {
          toastId: TOAST_ID,
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          position: 'bottom-right',
        }
      );
    };

    const onVisibilityChange = () => {
      if (!document.hidden) check();
    };

    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', check);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', check);
    };
  }, []);
}
